import React, { useState } from 'react';
import { useFormik } from 'formik';
import { TextField as TextInput } from '@material-ui/core';
import {
  KeyboardDatePicker as DatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { t } from 'src/i18n';
import PrimaryButton from 'src/components/PrimaryButton/PrimaryButton.component';
import * as Yup from 'yup';
import moment from 'moment';
import {
  useStyles,
  FormLabel,
  CenterRow,
  ButtonWrapper
} from '../Register.style';
import ErrorPopup from 'src/components/ErrorPopup/ErrorPopup';
import {
  setCookie,
  isKeyPressEnterEvent,
  parseJSON
} from 'src/utils/common.util';
import API from 'src/api/api';
import ErrorMessageWithIcon from 'src/components/ErrorMessageWithIcon/ErrorMessageWithIcon.component';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';

interface IProps {
  setRegisterSuccessful(isSuccessful: boolean): void;
}

// RFC 5322
const EMAIL_REGREX = /^(?=.{1,64}@)(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(?=.{1,255}$)((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

const RegisterForm: React.FC<IProps> = ({ setRegisterSuccessful }) => {
  const classes = useStyles();
  const [selectedDate, handleDateChange] = useState<any>(null);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [lastNameError, setLastNameError] = React.useState<string>('');
  const [dateOfBirthError, setDateOfBirthError] = React.useState<string>('');
  const [emailError, setEmailError] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
    setFieldError,
    setFieldValue
  } = useFormik({
    initialValues: {
      lastName: '',
      dob: '',
      email: ''
    },

    // tenant info accountId
    onSubmit: (val) => {
      setIsLoading(true);
      const accountId = (parseJSON('tenantInfo') as any).accountId;

      API()
        .put(`/account/v1/public/accounts/${accountId}/patients`, {
          birthDate: new Date(val.dob).toISOString(),
          email: val.email,
          lastName: val.lastName.trim().toLowerCase()
        })
        .then((response) => {
          if (response.status !== 200) {
            setIsLoading(false);

            return;
          }
          setCookie('setPasswordKey', response.data.key, { duration: 24 });
          setRegisterSuccessful(true);
          setIsLoading(false);
        })
        .catch((err) => {
          if (!err.response.status) {
            setIsLoading(false);

            return;
          }
          setErrorMessage(err.response.data.error);
          setShowErrorPopup(true);
          setIsLoading(false);
        });
    },

    validationSchema: Yup.object().shape({
      lastName: Yup.string().required(t('register.error.required.lastName')),
      dob: Yup.date()
        .required(t('register.error.required.dob'))
        .typeError(t('register.error.format.dob'))
        .max(new Date(), t('register.error.maxDate.dob')),

      email: Yup.string()
        .required(t('register.error.required.email'))
        .matches(EMAIL_REGREX, { message: t('register.error.format.email') })
    })
  });

  const handleOnChangeDate = (date) => {
    handleDateChange(date);
    setFieldError('dob', '');
  };

  const handleKeydown = (event) => {
    if (isKeyPressEnterEvent(event)) {
      handleSubmit();
    }

    return false;
  };

  const trimWhenBlur = (fieldname) => (e) => {
    const trimValue = e.target.value.trim();
    setFieldValue(fieldname, trimValue, true);
  };

  React.useEffect(() => {
    values.dob = !selectedDate ? '' : moment(selectedDate).format('YYYY-MM-DD');
  }, [selectedDate]);

  React.useEffect(() => {
    if (!!errors.lastName && !!touched.lastName) {
      setLastNameError(errors.lastName);
    } else {
      setLastNameError('');
    }

    if (!!errors.dob && !!touched.dob) {
      setDateOfBirthError(errors.dob);
    } else {
      setDateOfBirthError('');
    }

    if (!!errors.email && !!touched.email) {
      setEmailError(errors.email);
    } else {
      setEmailError('');
    }
  }, [errors, touched]);

  return (
    <LoadingContainer isLoading={isLoading} isOverlay={true}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className={classes.mb15}>
          <FormLabel>{t('register.label.lastName')} *</FormLabel>
          <TextInput
            onChange={handleChange}
            onKeyDown={handleKeydown}
            name="lastName"
            value={values.lastName}
            type="text"
            label={null}
            autoComplete="off"
            variant="outlined"
            placeholder={'Last Name'}
            inputProps={{
              maxLength: 50,
              style: { fontSize: 15, padding: 10.5 },
              onBlur: trimWhenBlur('lastName')
            }}
            style={{ width: '100%' }}
            error={!!errors.lastName && !!touched.lastName}
          />
          {lastNameError && (
            <ErrorMessageWithIcon message={lastNameError} size="small" />
          )}
        </div>

        <div className={classes.mb30}>
          <FormLabel className={classes.mt30}>
            {t('register.label.dob')} *
          </FormLabel>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <DatePicker
              id="dob"
              name="dob"
              value={selectedDate ? selectedDate : null}
              onChange={handleOnChangeDate}
              onKeyDown={handleKeydown}
              format={'MM/DD/YYYY'}
              inputVariant="outlined"
              style={{ width: '100%' }}
              disableFuture={true}
              data-testid="datePicker"
              onBlur={handleBlur}
              error={!!errors.dob && !!touched.dob}
              helperText={null}
              placeholder="MM/DD/YYYY"
              inputProps={{ style: { padding: 10.5 } }}
            />
          </MuiPickersUtilsProvider>
          {dateOfBirthError && (
            <ErrorMessageWithIcon message={dateOfBirthError} size="small" />
          )}
        </div>

        <div className={classes.mb15}>
          <FormLabel>{t('register.label.email')} *</FormLabel>
          <TextInput
            onChange={handleChange}
            onKeyDown={handleKeydown}
            name="email"
            value={values.email}
            type="text"
            label={null}
            autoComplete="off"
            variant="outlined"
            placeholder={'Email'}
            inputProps={{
              style: { fontSize: 15, padding: 10.5 },
              onBlur: trimWhenBlur('email')
            }}
            style={{ width: '100%' }}
            onBlur={handleBlur}
            error={!!errors.email && !!touched.email}
          />
          {emailError && (
            <ErrorMessageWithIcon message={emailError} size="small" />
          )}
        </div>

        <ErrorPopup
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          errorMessage={errorMessage}
        />

        <CenterRow>
          <ButtonWrapper>
            <PrimaryButton
              type="submit"
              variant="contained"
              isShowIcon={true}
              title={t('general.register')}
            />
          </ButtonWrapper>
        </CenterRow>
      </form>
    </LoadingContainer>
  );
};

export default RegisterForm;
